import { Button, Card, LoadingOverlay, Stack, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { createContext, useContext, useEffect } from "react";
import { useStopwatch } from "react-timer-hook";
import { toast } from "react-toastify";

import { useSession } from "@/hooks/@custom/useSession";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { OrderDetails } from "@/pages/App/Driver/DriverQueueStatus/components/Order";
import { useGetIpQuery } from "@/store/apis/ipify";
import {
  useGetCheckinsCompanyActivesByCompanyQuery,
  useGetDriverQueueLeaveStatusQuery,
  useGetQueueDriverPositionQuery,
  useGetQueueQuery,
  useGetSettingsByCompanyQuery,
  useSaveQueueDeleteMutation
} from "@/store/apis/nextcargo";
import { isStatusCode } from "@/utils";
import { cn } from "@/utils/cn";
import { getToleranceTime } from "@/utils/getToleranceTime";

/* ############################################################ */

type QueueStatusContextType = {
  position?: number;
  queueDate?: string;
  handleCancel: () => void;
  disabled: boolean;
  handleConfirmRemove: () => void;
};
const QueueStatusProvider = createContext({} as QueueStatusContextType);
export const useQueueStatus = () => {
  const context = useContext(QueueStatusProvider);
  if (!context) {
    throw new Error("useQueueStatus must be used within a QueueStatusProvider");
  }
  return context;
};

/* ############################################################ */

export const Route = createFileRoute("/app/agendamento/status")({
  component: DriverQueueStatus
});

function DriverQueueStatus() {
  const navigate = useNavigate();
  const [disabled, { toggle: toggleDisabled }] = useDisclosure(false);

  const { driver } = useSession();
  const driverId = String(driver?.id);
  const driverCompanyId = String(driver?.company.id);

  const { data: settingsCompany, isLoading: isLoadingSettings } = useGetSettingsByCompanyQuery({ id: Number(driverCompanyId)! }, { skip: !driverCompanyId });
  const { data: driverIp, isLoading: isLoadingDriverIp } = useGetIpQuery(undefined, {
    selectFromResult: (state) => ({
      ...state,
      data: state.data?.ip
    })
  });
  const { data: driverCheckIn, isLoading: isLoadingDriverCheckIn } = useGetCheckinsCompanyActivesByCompanyQuery(
    { id: Number(driverCompanyId) },
    {
      skip: !driverCompanyId,
      selectFromResult: (state) => {
        const activeCheckinDriver = state.data?.original.find((item) => item.queue?.driver.id === Number(driverId));
        const transformedData = {
          id: activeCheckinDriver?.id,
          driverQueueId: activeCheckinDriver?.queue.id,
          driverCheckInActive: activeCheckinDriver?.isPresent,
          checkInCreatedAt: activeCheckinDriver?.updatedAt
        };

        return {
          ...state,
          data: transformedData
        };
      }
    }
  );
  const {
    data: driverQueuePosition,
    isLoading: isLoadingDriverQueuePosition,
    error: driverQueuePositionError
  } = useGetQueueDriverPositionQuery({ id: Number(driverId) }, { skip: !driverId });
  const { data: driverQueueLeaveStatus, isLoading: isLoadingDriverQueueLeaveStatus } = useGetDriverQueueLeaveStatusQuery(
    { id: Number(driverId) },
    { skip: !driverId, pollingInterval: 1000 * 30 }
  );
  const isLoadingPromises = isLoadingDriverIp || isLoadingDriverCheckIn || isLoadingDriverQueuePosition || isLoadingSettings || isLoadingDriverQueueLeaveStatus;

  const driverQueueId = driverQueuePosition?.queue.id;

  const [saveQueueDeleteTrigger] = useSaveQueueDeleteMutation({ fixedCacheKey: "delete-queue" });
  const { data: queueData } = useGetQueueQuery({ id: driverQueueId! }, { skip: !driverQueueId, pollingInterval: 1000 * 30 });

  const authorizedIPS = settingsCompany?.checkinIp.split(",");
  const isDriverIPAuthorized = authorizedIPS?.includes(driverIp || "");
  const status = queueData?.status || false;

  const licensePlate = driverQueuePosition?.queue.truck.licensePlate;

  /* ############################################################ */

  function handleCancel() {
    MODAL_HANDLER.driver.leaveQueue();
  }

  async function handleConfirmRemove() {
    if (!driverQueuePosition?.queue) return;
    toggleDisabled();

    if (driverQueueLeaveStatus?.canLeave) {
      toast.error("Você não pode sair da fila no momento.");
      handleCancel();
      return;
    }

    const { error } = await saveQueueDeleteTrigger({ id: driverQueueId! });
    if (!error) {
      navigate({ to: "/app" });
      return;
    }

    toggleDisabled();
  }

  const position = driverQueuePosition?.position;
  const queueDate = dayjs(driverQueuePosition?.queue.date).isValid() ? dayjs(driverQueuePosition?.queue.date).format("DD [de] MMMM [às] HH:mm") : "";

  const isToleranceEnable = settingsCompany?.enableTolerance;
  const showToleranceTimer = isToleranceEnable && driverQueuePosition?.queue?.status === false;

  type ThemeType = "inTolerance" | "checkin" | "default";
  const themeColor = new Map<ThemeType, string>([
    ["inTolerance", "#fc8f00"],
    ["checkin", "#228be6"],
    ["default", "#de2a1c"]
  ]);
  const getTheme = () => {
    const now = dayjs();
    const innerQueueDate = dayjs(driverQueuePosition?.queue.date);
    const isQueueDateExpired = innerQueueDate.isBefore(now, "minute");
    const isQueueDateSameDay = innerQueueDate.isSame(now, "day");

    const isInTolerance = isQueueDateSameDay && isQueueDateExpired;

    if (isInTolerance) return themeColor.get("inTolerance");
    if (driverCheckIn?.driverCheckInActive) return themeColor.get("checkin");

    return themeColor.get("default");
  };

  useEffect(() => {
    if (!isLoadingPromises && driverQueuePositionError) {
      if (isStatusCode(driverQueuePositionError, 404)) {
        console.log(driverQueuePositionError);
        navigate({ to: "/app" });
        toast.warning("Você não possui nenhum agendamento no momento!");
      }
    }
  }, [isLoadingPromises, driverQueuePositionError]);

  if (isLoadingPromises) return <LoadingOverlay loaderProps={{ size: "xl", type: "bars" }} overlayProps={{ backgroundOpacity: 1 }} visible />;

  return (
    <QueueStatusProvider.Provider value={{ position, queueDate, handleCancel, disabled, handleConfirmRemove }}>
      <>
        {position !== 0 || !driverQueuePosition?.queue?.attended ? (
          <>
            <Card component={Stack} className="mx-auto w-full max-w-2xl" shadow="md" withBorder>
              {driverQueuePosition?.queue && (
                <>
                  {driverQueuePosition && (
                    <Stack
                      gap={0}
                      align="center"
                      className={cn("rounded-md! border-2! border-current! p-2! text-(--theme-color)! md:p-4!")}
                      __vars={{ "--theme-color": getTheme() }}
                    >
                      {position !== 0 && (
                        <Text size="2xl" fw="bold" ta="center" lh="normal" style={{ color: "currentColor" }}>
                          Sua posição agora é: <br />
                          <Text size="6xl" fw="bold" c="gray" lh="normal" style={{ color: "currentColor" }}>
                            {position}°
                          </Text>
                        </Text>
                      )}

                      {!status && !!queueDate && (
                        <Text size="4xl" fw="bold" ta="center" lh="normal" style={{ color: "currentColor" }} mb="sm">
                          Previsão de carregamento:
                        </Text>
                      )}

                      <div
                        className={cn({
                          "rounded-sm border-4 border-current p-1 font-mono text-3xl font-extrabold text-current uppercase md:border-3 md:text-6xl": true
                        })}
                      >
                        <Text lh="normal" size="6xl" fw="bold" c="currentColor" style={{ color: "currentColor" }}>
                          {licensePlate}
                        </Text>
                      </div>

                      {!status && <Text size="2xl">{queueDate}</Text>}
                      {showToleranceTimer && driverQueuePosition?.queue?.date && <ToleranceTimer date={driverQueuePosition.queue.date} tolerance={settingsCompany?.tolerance} />}
                    </Stack>
                  )}

                  {status && driverQueuePosition?.queue?.date && (
                    <Text ta="center" size="2xl" fw="bold">
                      Em atendimento...
                    </Text>
                  )}

                  {driverQueuePosition?.scheduling && <OrderDetails scheduling={driverQueuePosition.scheduling} />}
                </>
              )}

              {!status && (
                <Stack>
                  {driverQueuePosition?.queueVisibility && (
                    <Text size="xl" ta="justify">
                      A data e hora acima informada trata-se de uma <strong className="text-bold">previsão</strong> que pode ser antecipada ou adiada sem aviso prévio a depender da
                      dinâmica do fluxo de atendimento na indústria. Recomendamos a consulta constante de sua posição e horário de previsão, e a chegada ao local com 6 horas de
                      antecedência.
                    </Text>
                  )}

                  <div
                    className={cn("gap-2", {
                      "justify-center": driverQueuePosition?.$isDriverActionsBlocked,
                      "flex flex-wrap *:flex-[1_1_min(100%,200px)]": true
                    })}
                  >
                    {driverQueueLeaveStatus?.canLeave && (
                      <Button onClick={handleCancel} color="red" className="queue">
                        Sair da fila
                      </Button>
                    )}

                    <Button component={Link} to="/app/painel/fila" color="green">
                      Ver fila
                    </Button>

                    {settingsCompany?.enableCheckin && isDriverIPAuthorized && !driverCheckIn?.driverCheckInActive && (
                      <Button component={Link} to="/app/checkin" color="blue.6" className="checkin">
                        Fazer check-in
                      </Button>
                    )}
                  </div>
                </Stack>
              )}
            </Card>

            {/* <pre>
              <code>{JSON.stringify({ status, queueVisibility: driverQueuePosition?.queueVisibility, showUpdateQueueWarning, queueDate }, null, 2)}</code>
              <br />
              <code>{JSON.stringify(driverQueuePosition, null, 2)}</code>
            </pre> */}
          </>
        ) : (
          <Title order={1} ta="center">
            Você não possui marcação na fila
          </Title>
        )}
      </>
    </QueueStatusProvider.Provider>
  );
}

function ToleranceTimer({ date, tolerance = "00:00" }: { date: string; tolerance?: string }) {
  const { time, isInTolerance, isExhausted } = getToleranceTime({ currentData: date, toleranceTime: tolerance });
  const { reset } = useStopwatch({ autoStart: isInTolerance });

  useEffect(() => {
    return reset;
  }, []);

  if (isExhausted)
    return (
      <div className="flex flex-col items-center gap-2 md:flex-row md:items-baseline">
        <Text fw="bold" size="3xl" lh="normal">
          Tolerância:
        </Text>

        <Text fw="bold" size="6xl" lh="normal">
          {time}
        </Text>
      </div>
    );

  if (!isInTolerance) return null;

  return (
    <div className="flex flex-col items-center gap-2 md:flex-row md:items-baseline">
      <Text fw="bold" size="3xl" lh="normal">
        Tolerância:
      </Text>

      <Text fw="bold" size="6xl" lh="normal">
        {time}
      </Text>
    </div>
  );
}

function Reschedule() {
  const { position, queueDate, handleCancel } = useQueueStatus();

  return (
    <div className="gap-2 text-gray-700">
      <Stack align="center" justify="center">
        <Title order={1}>Mudança de posição na fila</Title>

        <Title order={2} ta="center">
          Deseja <strong>remarcar</strong> seu agendamento?
        </Title>
      </Stack>

      <div className="my-3 flex aspect-square flex-col items-center justify-center rounded-md bg-gray-200 p-2 shadow-md md:p-4">
        <Text size="4xl" fw="bolder">
          {position}°
        </Text>

        <Text ta="center" size="xl">
          Posição atual
        </Text>
      </div>

      <Stack gap="sm" align="center" justify="center">
        <Title order={3}>
          Previsão de carregamentooooo: <strong className="tabular-nums">{queueDate}</strong>
        </Title>

        <Text size="sm" ta="center">
          A data e hora acima informada trata-se de uma previsão que pode ser antecipada ou adiada sem aviso prévio a depender da dinâmica do fluxo de atendimento na indústria.
          Recomendamos a consulta constante de sua posição e horário de previsão, e a chegada ao local com 6 horas de antecedência.
        </Text>
      </Stack>

      <Button color="red" onClick={handleCancel} fullWidth>
        Não
      </Button>
    </div>
  );
}
